import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2, Trophy, Target, ShoppingCart, Crown, Swords, Briefcase, Gift, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { fetchFiscalLabels, docInfo, docValid } from '../../lib/fiscalDocs';
import { CountrySelect } from '../CountrySelect';
import { PhoneInput } from '../PhoneInput';
import { COUNTRY_DIALS } from '../../lib/countryDials';

const CheckoutDialog = ({  paymentNotice,
  availablePaymentMethods,
  config,
  countInRanking,
  customerAccount,
  customerData,
  error,
  formatCPF,
  handleCancelConfirmation,
  handleChangeUser,
  handleConfirmPayment,
  kakoId,
  loading,
  rememberCustomerData,
  selectedPackage,
  selectedPaymentMethod,
  setCountInRanking,
  setCustomerData,
  setRememberCustomerData,
  setSelectedPaymentMethod,
  showConfirmation,
  userInfo,
  validateCPF,
}) => {
  const [fiscalLabels, setFiscalLabels] = useState(null);
  const [mbwayQuote, setMbwayQuote] = useState(null);
  // PIX é exclusivo do Brasil: internacional só vê o método do país dele
  const intl = customerAccount ? !!customerAccount.is_foreigner : !!customerData.is_foreigner;
  // MB WAY tem taxa fixa por transação: só aparece a partir do pacote mínimo
  const mbwayMin = availablePaymentMethods.mbwayMinCoins || 0;
  const mbwayPacoteOk = (selectedPackage?.coins || 0) >= mbwayMin;
  const metodos = {
    pix: availablePaymentMethods.pix && !intl,
    mbway: availablePaymentMethods.mbway && intl && mbwayPacoteOk,
  };
  const semMetodo = !metodos.pix && !metodos.mbway;
  const num2 = (v) => (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // documento fiscal segue a regra do país escolhido (mesma do cadastro)
  const docFiscal = docInfo(fiscalLabels, customerData.country);
  const docOk = docValid(fiscalLabels, customerData.country, customerData.document);
  useEffect(() => { fetchFiscalLabels().then(setFiscalLabels); }, []);

  // Cotação em euro (valor do pacote + taxa MB WAY) para mostrar a conta aberta
  useEffect(() => {
    if (selectedPaymentMethod !== 'mbway' || !selectedPackage?.id) { setMbwayQuote(null); return; }
    let ativo = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payment-methods/mbway-quote`,
      { params: { package_id: selectedPackage.id } })
      .then((r) => ativo && setMbwayQuote(r.data))
      .catch(() => ativo && setMbwayQuote(null));
    return () => { ativo = false; };
  }, [selectedPaymentMethod, selectedPackage?.id]);

  // Trocar de país no formulário não pode deixar um método inválido selecionado
  useEffect(() => {
    if (intl && selectedPaymentMethod === 'pix') {
      setSelectedPaymentMethod(metodos.mbway ? 'mbway' : null);
    }
    if (!intl && selectedPaymentMethod === 'mbway') {
      setSelectedPaymentMethod(metodos.pix ? 'pix' : null);
    }
  }, [intl, selectedPaymentMethod, metodos.mbway, metodos.pix, setSelectedPaymentMethod]);

  return (
    <Dialog open={showConfirmation} onOpenChange={() => {}}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto [&>button]:hidden">
          <DialogTitle className="sr-only">Confirmação de compra</DialogTitle>
          <div className="text-center space-y-4">
            
            {/* DADOS DO CLIENTE - COMPACTO */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-left space-y-3">
              <p className="text-sm font-bold text-blue-900">📝 DADOS PARA PAGAMENTO</p>

              {customerAccount && (
                <div className="bg-white border border-blue-200 rounded-lg p-2.5 text-left space-y-1" data-testid="checkout-account-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Pagador</span>
                    <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">✅ conta verificada</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-800">{customerData.name}</p>
                  <p className="text-xs text-gray-600" data-testid="checkout-payer-document">
                    {customerAccount.is_foreigner ? (
                      <>
                        {docInfo(fiscalLabels, customerAccount.country).label}{' '}
                        {customerData.document
                          ? `${customerData.document.slice(0, 2)}***${customerData.document.slice(-2)}`
                          : '—'}
                        {customerAccount.country ? ` · 🌍 ${customerAccount.country}` : ''}
                      </>
                    ) : (
                      <>CPF {customerData.cpf ? `${customerData.cpf.slice(0,3)}.***.***-${customerData.cpf.slice(-2)}` : '—'}</>
                    )}
                    {' · '}📱 {customerData.whatsapp_country || '+55'} {customerData.whatsapp}
                  </p>
                  <p className="text-xs text-gray-600 truncate">✉️ {customerData.email || '—'}</p>
                  <p className="text-[10px] text-gray-400 pt-0.5">Para alterar estes dados, acesse "Minha Conta".</p>
                </div>
              )}

              {!customerAccount && (<>
              {/* Nome */}
              <div className="space-y-1">
                <Label className="text-xs text-blue-700 font-semibold">Nome Completo *</Label>
                <Input
                  placeholder="JOÃO SILVA"
                  value={customerData.name}
                  onChange={(e) => setCustomerData({...customerData, name: e.target.value.toUpperCase()})}
                  className="text-sm uppercase h-9"
                  required
                />
                {customerData.name && customerData.name.trim().split(/\s+/).length < 2 && (
                  <p className="text-xs text-red-600">Digite nome e sobrenome</p>
                )}
              </div>

              {/* Toggle Brasileiro/Estrangeiro - Visual melhorado */}
              <div 
                data-testid="checkout-foreigner-toggle"
                className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                  customerData.is_foreigner 
                    ? 'bg-purple-100 border-2 border-purple-400' 
                    : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                }`}
                onClick={() => {
                  const virandoIntl = !customerData.is_foreigner;
                  setCustomerData({
                    ...customerData,
                    is_foreigner: virandoIntl,
                    cpf: virandoIntl ? '' : customerData.cpf,
                    document: '',
                    country: virandoIntl ? '' : '',
                    whatsapp_country: virandoIntl ? customerData.whatsapp_country : '+55',
                  });
                }}
              >
                <div className={`w-5 h-5 rounded-md flex items-center justify-center text-white text-xs font-bold ${
                  customerData.is_foreigner ? 'bg-purple-500' : 'bg-gray-300'
                }`}>
                  {customerData.is_foreigner ? '✓' : ''}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${customerData.is_foreigner ? 'text-purple-700' : 'text-gray-600'}`}>
                    🌍 Sou de fora do Brasil
                  </p>
                  <p className="text-xs text-gray-500">Portugal, Angola, Moçambique, etc</p>
                </div>
              </div>

              {/* País + documento fiscal do país (mesmo padrão do cadastro) */}
              <div className="space-y-1">
                {customerData.is_foreigner ? (
                  <>
                    <Label className="text-xs text-purple-700 font-semibold flex items-center gap-1">
                      <span>🌍</span> País *
                    </Label>
                    <CountrySelect testIdPrefix="checkout-country" exclude={['Brasil']}
                      value={customerData.country || ''}
                      onChange={(pais) => setCustomerData({
                        ...customerData, country: pais, document: '',
                        whatsapp_country: `+${(COUNTRY_DIALS.find((c) => c.n === pais) || {}).d || '55'}`,
                      })} />
                    <Label className="mt-2 flex items-center gap-1 text-xs font-semibold text-purple-700">
                      <span>📄</span> {docFiscal.label} *
                    </Label>
                    <Input
                      placeholder={docFiscal.label}
                      value={customerData.document || ''}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        document: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                      })}
                      className="text-sm uppercase h-9 border-purple-200 focus:border-purple-400"
                      data-testid="checkout-fiscal-doc"
                      disabled={!customerData.country}
                      required
                    />
                    <p className="text-xs text-purple-500">
                      {customerData.country
                        ? `${docFiscal.label} de ${customerData.country} · ${docFiscal.hint}`
                        : 'Escolha o país para informar o documento'}
                    </p>
                    {fiscalLabels && customerData.country && customerData.document && !docOk && (
                      <p className="text-xs text-red-600" data-testid="checkout-fiscal-doc-error">
                        {docFiscal.label} inválido · {docFiscal.hint}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Label className="text-xs text-blue-700 font-semibold">CPF *</Label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      placeholder="000.000.000-00"
                      value={formatCPF(customerData.cpf)}
                      onChange={(e) => {
                        const cpf = e.target.value.replace(/\D/g, '');
                        if (cpf.length <= 11) {
                          setCustomerData({...customerData, cpf});
                        }
                      }}
                      maxLength={14}
                      className="text-sm h-9"
                      required
                    />
                    {customerData.cpf && customerData.cpf.length === 11 && !validateCPF(customerData.cpf) && (
                      <p className="text-xs text-red-600">CPF Inválido</p>
                    )}
                  </>
                )}
              </div>

              {/* WhatsApp / Telefone */}
              <div className="space-y-1">
                <Label className="text-xs text-blue-700 font-semibold">
                  {selectedPaymentMethod === 'mbway' ? 'Telefone MB WAY (Portugal) *' : 'WhatsApp *'}
                </Label>
                <PhoneInput testIdPrefix="checkout-whatsapp"
                  countryName={customerData.is_foreigner ? customerData.country : 'Brasil'}
                  dial={(customerData.whatsapp_country || '+55').replace('+', '')}
                  number={customerData.whatsapp}
                  onChange={({ dial, number }) => setCustomerData({
                    ...customerData, whatsapp_country: `+${dial}`,
                    whatsapp: (number || '').replace(/\D/g, '').slice(0, 15),
                  })} />
                <p className="text-xs text-gray-500">
                  {(customerData.whatsapp_country || '+55') === '+55'
                    ? 'DDD + número (ex: 11 99999-9999)'
                    : 'Número completo do seu país (sem o código internacional)'}
                </p>
                {selectedPaymentMethod === 'mbway' && (
                  <p className="text-xs text-indigo-500">Use o número associado ao MB WAY (Portugal · 9 dígitos)</p>
                )}
              </div>

              {/* Email (opcional para quem não tem conta — usado por alguns pagamentos) */}
              <div className="space-y-1">
                <Label className="text-xs text-blue-700 font-semibold">E-mail (opcional)</Label>
                <Input
                  type="email"
                  inputMode="email"
                  placeholder="voce@email.com"
                  value={customerData.email || ''}
                  onChange={(e) => setCustomerData({...customerData, email: e.target.value.toLowerCase().trim()})}
                  className="text-sm h-9"
                  data-testid="checkout-email-input"
                />
              </div>
              </>)}

              {/* 2 CHECKBOXES NA MESMA CAIXA */}
              <div className="space-y-1.5 mt-2">
                {/* Lembrar Dados */}
                <div className={`border rounded p-1.5 text-xs ${
                  rememberCustomerData ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'
                }`}>
                  <div className="flex items-start gap-1.5">
                    <Checkbox
                      id="remember-customer"
                      checked={rememberCustomerData}
                      onCheckedChange={setRememberCustomerData}
                      className="mt-0.5"
                    />
                    <Label htmlFor="remember-customer" className={`cursor-pointer leading-tight ${
                      rememberCustomerData ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {rememberCustomerData ? '💾 Lembrar dados' : '✗ Não lembrar dados'}
                    </Label>
                  </div>
                </div>

                {/* Aparecer no Ranking/Scroll */}
                {(config?.ranking_active || config?.scrolling_text_active) && (
                  <div className={`border rounded p-1.5 text-xs ${
                    countInRanking ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'
                  }`}>
                    <div className="flex items-start gap-1.5">
                      <Checkbox
                        id="count-ranking"
                        checked={countInRanking}
                        onCheckedChange={setCountInRanking}
                        className="mt-0.5"
                      />
                      <Label htmlFor="count-ranking" className={`cursor-pointer leading-tight ${
                        countInRanking ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {countInRanking ? '🏆 Aparecer no ranking/scroll' : '✗ Não aparecer no ranking/scroll'}
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {((semMetodo && !(availablePaymentMethods.mbway && intl && !mbwayPacoteOk)) || paymentNotice) && (
              <div className="rounded-lg border-2 border-red-300 bg-red-50 p-2.5 text-left"
                data-testid="checkout-payment-notice">
                <p className="text-xs font-semibold text-red-800">
                  🌍 {paymentNotice || (intl
                    ? 'O meio de pagamento do seu país ainda não está disponível. Fale com o suporte para concluir a compra.'
                    : 'Nenhum meio de pagamento disponível agora. Tente novamente em instantes.')}
                </p>
              </div>
            )}

            {availablePaymentMethods.mbway && intl && !mbwayPacoteOk && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2.5 text-left">
                <p className="text-[11px] leading-relaxed text-indigo-800" data-testid="mbway-min-notice">
                  📱 O MB WAY está disponível a partir de {mbwayMin.toLocaleString('pt-BR')} moedas —
                  a taxa fixa de € 0,25 por pagamento fica alta em pacotes pequenos.
                  Escolha um pacote maior para pagar em euro.
                </p>
              </div>
            )}

            {/* SELEÇÃO DE MÉTODO DE PAGAMENTO */}
            {!semMetodo && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-left">
                <p className="text-sm font-bold text-purple-900 mb-2">MÉTODO DE PAGAMENTO</p>
                <div className={`grid gap-2 ${
                  [metodos.pix, metodos.mbway].filter(Boolean).length >= 3
                    ? 'grid-cols-3' : 'grid-cols-2'
                }`}>
                  {/* PIX (só Brasil) */}
                  {metodos.pix && (
                    <button
                      type="button"
                      onClick={() => setSelectedPaymentMethod('pix')}
                      data-testid="payment-method-pix"
                      className={`relative overflow-hidden rounded-xl border-2 transition-all h-28 ${
                        selectedPaymentMethod === 'pix'
                          ? 'border-cyan-500 ring-2 ring-cyan-300 scale-[1.02]'
                          : 'border-gray-200 hover:border-cyan-300'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#00b4d8] to-[#0077b6]" />
                      <img 
                        src="https://customer-assets.emergentagent.com/job_2cfdfe5d-5b87-4072-8829-ddaedcfedb94/artifacts/c9vo4jy4_IMG_9401.PNG" 
                        alt="PIX"
                        className="absolute inset-0 w-full h-full object-contain p-2"
                      />
                      <div className="absolute bottom-0 inset-x-0 bg-black/60 backdrop-blur-sm py-1 text-center">
                        <p className="text-white/90 text-[10px] font-semibold">R$ {selectedPackage?.price?.toFixed(2) || '0.00'}</p>
                      </div>
                    </button>
                  )}
                  
                  {/* MB WAY (Portugal) */}
                  {metodos.mbway && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPaymentMethod('mbway');
                        setCustomerData(prev => ({...prev, is_foreigner: true}));
                      }}
                      data-testid="payment-method-mbway"
                      className={`relative overflow-hidden rounded-xl border-2 transition-all h-28 ${
                        selectedPaymentMethod === 'mbway'
                          ? 'border-red-500 ring-2 ring-red-300 scale-[1.02]'
                          : 'border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <div className="absolute inset-0 bg-white" />
                      <img
                        src="/mbway-logo.png?v=2"
                        alt="MB WAY"
                        className="absolute inset-0 w-full h-full object-contain p-2"
                      />
                      <div className="absolute bottom-0 inset-x-0 bg-black/70 backdrop-blur-sm py-1 text-center">
                        <p className="text-white/90 text-[10px] font-semibold">
                          {mbwayQuote ? `€ ${num2(mbwayQuote.amount_eur)}` : 'EUR (Portugal)'}
                        </p>
                      </div>
                    </button>
                  )}
                </div>
                {selectedPaymentMethod === 'mbway' && mbwayQuote && (
                  <div className="mt-2 rounded-lg border border-indigo-200 bg-white p-2 text-[11px] leading-relaxed text-gray-700"
                    data-testid="mbway-fee-breakdown">
                    <p className="font-bold text-indigo-900">
                      Total: € {num2(mbwayQuote.amount_eur)}
                      <span className="font-normal text-gray-500">
                        {' '}(inclui € {num2(mbwayQuote.fee_eur)} de taxa MB WAY)
                      </span>
                    </p>
                    <p className="text-gray-500">
                      Pacote R$ {num2(mbwayQuote.amount_brl)} · câmbio 1 € = R$ {num2(mbwayQuote.mid_rate)}
                      {' '}· taxa do MB WAY {num2(mbwayQuote.fee_pct)}% + € {num2(mbwayQuote.fee_fixed_eur)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ALERTA COMPACTO */}
            <div className="bg-red-600 border-2 border-red-700 rounded-lg p-2.5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-bold text-white">⚠️ CONFIRMAÇÃO:</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleChangeUser}
                  className="bg-yellow-200 hover:bg-yellow-300 border-yellow-400 text-xs font-bold h-6 px-2"
                >
                  Trocar
                </Button>
              </div>
              
              {/* Avatar e Nome inline */}
              <div className="flex items-center gap-2 mb-2">
                {userInfo?.avatar && (
                  <img
                    src={userInfo.avatar}
                    alt="Avatar"
                    className="w-8 h-8 rounded-full border-2 border-yellow-300"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-xs truncate">{userInfo?.nickname}</p>
                  <p className="text-xs text-yellow-300">ID: {kakoId}</p>
                </div>
              </div>
              
              {/* Moedas e Valor lado a lado */}
              <div className="flex items-center justify-between bg-white/10 rounded p-1.5">
                <div>
                  <p className="text-lg font-bold text-yellow-300">{selectedPackage?.coins} 🪙</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-white">
                    R$ {selectedPackage?.price?.toFixed(2)}
                  </p>
                  {selectedPaymentMethod === 'mbway' && (
                    <p className="text-xs text-yellow-300">
                      {mbwayQuote ? `€ ${mbwayQuote.amount_eur.toFixed(2)} via MB WAY` : 'Valor em EUR será calculado'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Alerta de erro */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Botões */}
            <div className="flex gap-3">
              <Button
                onClick={handleCancelConfirmation}
                variant="outline"
                className="flex-1"
                size="lg"
              >
                Voltar
              </Button>
              <Button
                onClick={handleConfirmPayment}
                className="flex-1 bg-green-600 hover:bg-green-700"
                size="lg"
                disabled={loading || semMetodo}
                data-testid="checkout-confirm-button"
              >
                {loading ? <Loader2 className="animate-spin" />
                  : semMetodo ? 'Pagamento indisponível' : '✓ Confirmar Pagamento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
};

export default CheckoutDialog;
